import React from "react";
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Slide,
  useMediaQuery
} from "@mui/material";
import {
  Close as CloseIcon,
  Assignment as DefaultIcon
} from "@mui/icons-material";
import { styled, useTheme } from "@mui/material/styles";
import StandardButton from './StandardButton'; // Importar o novo componente

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 20,
    minWidth: 500,
    maxWidth: 600,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    [theme.breakpoints.down('md')]: {
      minWidth: '90vw',
      maxWidth: '95vw',
      margin: theme.spacing(1),
    },
  },
}));

const DialogTitleStyled = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  textAlign: 'center',
  padding: theme.spacing(3, 3, 2, 3),
  position: 'relative',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const TitleIcon = styled(Box)(({ theme, iconcolor }) => ({
  backgroundColor: iconcolor || theme.palette.primary.main,
  borderRadius: '50%',
  width: 60,
  height: 60,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px auto',
  color: 'white',
}));

const CloseButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  minWidth: 40,
  width: 40,
  height: 40,
  borderRadius: '50%',
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
  '&:disabled': {
    color: theme.palette.text.disabled,
  },
}));

const DialogContentStyled = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3),
  background: theme.palette.background.paper,
  position: 'relative',
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 16,
  zIndex: 10,
}));

// ============================================================================
// COMPONENTE PRINCIPAL - ATUALIZADO com StandardButton
// ============================================================================

const StandardModal = ({
  // Controle do modal
  open,
  onClose,
  
  // Conteúdo
  title,
  subtitle,
  children,
  
  // Configurações visuais
  icon: IconComponent = DefaultIcon,
  iconColor,
  maxWidth = "md",
  fullWidth = true,
  
  // Ações
  actions = [],
  
  // Estados
  loading = false,
  
  // Comportamento
  disableEscapeKeyDown = false,
  disableBackdropClick = false,
  showCloseButton = true,
  
  // Transição
  transitionDirection = "up",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleClose = (event, reason) => {
    if (loading) return;
    
    if (disableBackdropClick && reason === 'backdropClick') return;
    if (disableEscapeKeyDown && reason === 'escapeKeyDown') return;
    
    onClose();
  };

  const handleActionClick = (action) => {
    if (action.disabled || action.loading) return;
    action.onClick();
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      TransitionComponent={Slide}
      TransitionProps={{ direction: transitionDirection }}
      disableEscapeKeyDown={disableEscapeKeyDown}
    >
      {/* Header */}
      <DialogTitleStyled>
        {showCloseButton && (
          <CloseButton 
            onClick={() => onClose()} 
            disabled={loading}
            aria-label="Fechar modal"
          >
            <CloseIcon />
          </CloseButton>
        )}
        
        <TitleIcon iconcolor={iconColor}>
          <IconComponent sx={{ fontSize: 32 }} />
        </TitleIcon>
        
        <Typography variant="h5" component="h2" fontWeight={600}>
          {title}
        </Typography>
        
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {subtitle}
          </Typography>
        )}
      </DialogTitleStyled>

      {/* Content */}
      <DialogContentStyled>
        {loading && (
          <LoadingOverlay>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Carregando...
            </Typography>
          </LoadingOverlay>
        )}
        
        {children}
      </DialogContentStyled>

      {/* Actions - ATUALIZADO para usar StandardButton */}
      {actions.length > 0 && (
        <DialogActions sx={{ p: 3, gap: 2 }}>
          {actions.map((action, index) => (
            <StandardButton
              key={index}
              variant={action.variant || 'outlined'}
              color={action.color || 'primary'}
              onClick={() => handleActionClick(action)}
              disabled={action.disabled || loading}
              loading={action.loading}
              startIcon={action.startIcon}
              endIcon={action.endIcon}
            >
              {action.label}
            </StandardButton>
          ))}
        </DialogActions>
      )}
    </StyledDialog>
  );
};

// ============================================================================
// PROP TYPES
// ============================================================================

StandardModal.propTypes = {
  // Controle do modal
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  
  // Conteúdo
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  
  // Configurações visuais
  icon: PropTypes.elementType,
  iconColor: PropTypes.string,
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', false]),
  fullWidth: PropTypes.bool,
  
  // Ações
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      variant: PropTypes.oneOf(['text', 'outlined', 'contained']),
      color: PropTypes.oneOf(['inherit', 'primary', 'secondary', 'success', 'error', 'info', 'warning']),
      disabled: PropTypes.bool,
      loading: PropTypes.bool,
      startIcon: PropTypes.node,
      endIcon: PropTypes.node,
    })
  ),
  
  // Estados
  loading: PropTypes.bool,
  
  // Comportamento
  disableEscapeKeyDown: PropTypes.bool,
  disableBackdropClick: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  
  // Transição
  transitionDirection: PropTypes.oneOf(['up', 'down', 'left', 'right']),
};

export default StandardModal;